import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Menu, X, Phone, MapPin } from 'lucide-react';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Services', href: '/services' },
  { name: 'Products', href: '/products' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'Reviews', href: '/reviews' },
  { name: 'Contact', href: '/contact' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/98 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      {/* Top Bar */}
      <div className={`${isScrolled ? 'hidden' : 'block'} bg-slate-900 text-white py-2`}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:08512853955" className="flex items-center gap-2 hover:text-orange-400 transition-colors">
              <Phone className="w-4 h-4" />
              <span>085128 53955</span>
            </a>
            <span className="hidden md:flex items-center gap-2 text-slate-400">
              <MapPin className="w-4 h-4" />
              <span>Ballabhgarh, Faridabad</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-yellow-400 flex items-center gap-1">
              <span className="text-yellow-400">★</span> 5.0 Google Rating
            </span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-sky-500 to-orange-500 rounded-lg flex items-center justify-content text-white font-bold text-lg md:text-xl font-['Oswald']">
              <span className="w-full text-center">R</span>
            </div>
            <div className={`transition-colors ${isScrolled ? 'text-slate-900' : 'text-white'}`}>
              <h1 className="font-['Oswald'] font-bold text-lg md:text-xl leading-tight">RAJ ALUMINUM</h1>
              <p className="text-xs opacity-80">Glass & Hardware</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase()}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive(item.href)
                    ? isScrolled
                      ? 'text-sky-600 bg-sky-50'
                      : 'text-orange-400 bg-white/10'
                    : isScrolled
                    ? 'text-slate-700 hover:text-sky-600 hover:bg-slate-100'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a href="tel:08512853955">
              <Button
                variant="outline"
                size="sm"
                data-testid="header-call-btn"
                className={`${
                  isScrolled
                    ? 'border-slate-300 text-slate-700 hover:bg-slate-100'
                    : 'border-white/30 text-white hover:bg-white/10'
                }`}
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
            <Link to="/contact">
              <Button
                size="sm"
                data-testid="header-quote-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                Get Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="mobile-menu-btn"
                  className={isScrolled ? 'text-slate-900' : 'text-white'}
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-slate-900 border-slate-800 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-slate-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold font-['Oswald']">
                        R
                      </div>
                      <div className="text-white">
                        <h2 className="font-['Oswald'] font-bold">RAJ ALUMINUM</h2>
                        <p className="text-xs text-slate-400">Glass & Hardware</p>
                      </div>
                    </div>
                  </div>
                  
                  <nav className="flex-1 p-4">
                    {navigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                        className={`block px-4 py-3 rounded-lg text-base font-medium transition-all mb-1 ${
                          isActive(item.href)
                            ? 'text-orange-400 bg-white/10'
                            : 'text-white/80 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </nav>

                  <div className="p-4 border-t border-slate-800 space-y-3">
                    <a href="tel:08512853955" className="block">
                      <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                        <Phone className="w-4 h-4 mr-2" />
                        Call Now
                      </Button>
                    </a>
                    <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                        Get Quote
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
